import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { defectsApi, assetsApi, usersApi, workOrdersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useLookup } from '../hooks/useLookups';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { StatusPill } from '../components/ui/StatusPill';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AssetCombobox from '../components/assets/AssetCombobox';
import { SlideDrawer } from '../components/ui/slide-drawer';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { ActionMenu } from '../components/ui/action-menu';
import { DensityToggle, SortableTh, useDensity, useSortState } from '../components/ui/list-controls';
import { 
  isToday, 
  isThisWeek, 
  isThisMonth, 
  startOfWeek, 
  endOfWeek, 
  subWeeks, 
  startOfMonth, 
  endOfMonth, 
  subMonths,
  startOfDay,
  endOfDay,
  isWithinInterval,
  parseISO
} from 'date-fns';
import { 
  ArrowLeft, 
  Loader2, 
  AlertTriangle, 
  Plus,
  Search,
  Camera,
  User,
  Clock,
  Package,
  LayoutGrid,
  List,
  X,
  Eye,
  Edit,
  Trash2,
  Wrench,
  Link as LinkIcon,
  QrCode,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

// Helper function to get proper image source URL with auth token
// Handles: data URLs, base64 strings, and Azure Blob URLs (via proxy with token)
const getPhotoSrc = (photo, token) => {
  if (!photo) return '';
  // Already a data URL
  if (photo.startsWith('data:')) return photo;
  // Azure Blob URL - use proxy endpoint with token for authentication
  if (photo.includes('blob.core.windows.net')) {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
    return `${apiUrl}/api/blob-proxy?url=${encodeURIComponent(photo)}${tokenParam}`;
  }
  // HTTP/HTTPS URL (other sources)
  if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
  // Assume base64 string without data URL prefix
  return `data:image/jpeg;base64,${photo}`;
};

const DATE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
];

// Dynamic badge components using lookups
const SeverityBadge = ({ severity, severities }) => {
  const config = severities?.find((s) => s.value === severity);
  const label = config?.name || severity?.replace(/_/g, ' ') || 'Unknown';
  // Severity → tone: critical/high → red, medium → amber, low → emerald.
  // The StatusPill resolves these via its built-in mapping or via the
  // explicit `tone` prop for ones it doesn't know yet.
  const toneByValue = { critical: 'red', high: 'red', medium: 'amber', low: 'emerald' };
  return <StatusPill value={severity} label={label} tone={toneByValue[severity]} />;
};

const StatusBadge = ({ status, statuses }) => {
  const config = statuses?.find((s) => s.value === status);
  const label = config?.name || status?.replace(/_/g, ' ') || 'Unknown';
  return <StatusPill value={status} label={label} />;
};

// Defect Form Component
const DefectForm = ({ assets, severities, preSelectedAsset, onSuccess, onCancel }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    asset_id: preSelectedAsset || '',
    title: '',
    description: '',
    severity: severities?.[0]?.value || 'medium',
    photos: [],
  });

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error('Please select an asset');
      return;
    }

    setSubmitting(true);
    try {
      await defectsApi.create(formData);
      toast.success('Defect reported');
      onSuccess();
    } catch (error) {
      toast.error('Failed to report defect');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label>Asset *</Label>
        <AssetCombobox
          assets={assets}
          value={formData.asset_id || null}
          onChange={(v) => setFormData(prev => ({ ...prev, asset_id: v || '' }))}
          placeholder="Select asset"
          className="w-full"
          testId="asset-select"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Brief description of the defect"
          required
          className="input-industrial"
          data-testid="defect-title-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Detailed description of the defect..."
          required
          rows={4}
          data-testid="defect-description-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Severity *</Label>
        <Select 
          value={formData.severity} 
          onValueChange={(v) => setFormData(prev => ({ ...prev, severity: v }))}
        >
          <SelectTrigger className="input-industrial" data-testid="severity-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {severities.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Photos</Label>
        <div className="flex flex-wrap gap-3">
          {formData.photos.map((photo, index) => (
            <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden">
              <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
              >
                <span className="text-white text-xs">×</span>
              </button>
            </div>
          ))}
          <Label className="w-20 h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary">
            <Camera className="w-5 h-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground mt-1">Add</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoCapture}
              className="hidden"
            />
          </Label>
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="submit" className="btn-industrial flex-1" disabled={submitting} data-testid="submit-defect-btn">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          {submitting ? 'Submitting...' : 'Report Defect'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-btn">
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Defect Detail/Edit View Component
const DefectDetailView = ({ defect, asset, users, severities, statuses, onUpdate, onClose, onRefresh, onDelete }) => {
  const navigate = useNavigate();
  const { isAdmin, isMechanic, token } = useAuth();
  const { formatDateTime } = useSettings();
  const [updating, setUpdating] = useState(false);
  const [openWorkOrders, setOpenWorkOrders] = useState([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [linkingToWO, setLinkingToWO] = useState(false);

  // Load open work orders for this asset
  useEffect(() => {
    if (defect?.asset_id && !defect.work_order_id) {
      loadOpenWorkOrders();
    }
  }, [defect?.asset_id, defect?.work_order_id]);

  const loadOpenWorkOrders = async () => {
    setLoadingWorkOrders(true);
    try {
      const response = await workOrdersApi.getOpenForAsset(defect.asset_id);
      setOpenWorkOrders(response.data);
    } catch (error) {
      console.error('Failed to load open work orders:', error);
    } finally {
      setLoadingWorkOrders(false);
    }
  };

  const handleUpdate = async (data) => {
    setUpdating(true);
    try {
      await onUpdate(data);
    } finally {
      setUpdating(false);
    }
  };

  const handleLinkToWorkOrder = async (workOrderId) => {
    setLinkingToWO(true);
    try {
      await workOrdersApi.linkDefect(workOrderId, defect.id);
      toast.success('Defect linked to work order');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link defect');
    } finally {
      setLinkingToWO(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{defect.title}</h2>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <SeverityBadge severity={defect.severity} severities={severities} />
          <StatusBadge status={defect.status} statuses={statuses} />
          {asset && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#E6E2D9] text-[12px] font-semibold text-[#59554D]">
              <Package className="w-3.5 h-3.5" />
              {asset.unit_number || asset.name}
            </span>
          )}
        </div>
        <p className="text-sm text-[#59554D] mt-3">
          Reported by{' '}
          <span className="text-[#1A1A1A] font-semibold" data-testid="defect-detail-reporter">
            {defect.reported_by_name || defect.created_by_name || 'Unknown'}
          </span>
          {' '}on <span className="font-mono text-[#1A1A1A]">{formatDateTime(defect.created_at)}</span>
          {defect.source === 'public_qr' && (
            <span
              className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-[5px] bg-[#FFF7DA] text-[#8A6D0F] align-middle"
              data-testid="defect-detail-qr-pill"
              title="Reported via the public QR-code page"
            >
              <QrCode className="w-3 h-3" />
              Via QR
            </span>
          )}
        </p>
        {(defect.reporter_phone || defect.reporter_email) && (
          <p className="text-sm mt-1">
            <span className="text-[#8A857A]">Contact </span>
            {defect.reporter_phone && (
              <a href={`tel:${defect.reporter_phone.replace(/\s/g, '')}`} className="text-[#B45309] hover:underline font-mono" data-testid="defect-detail-reporter-phone">{defect.reporter_phone}</a>
            )}
            {defect.reporter_phone && defect.reporter_email && <span className="text-[#8A857A]"> · </span>}
            {defect.reporter_email && (
              <a href={`mailto:${defect.reporter_email}?subject=Re: ${encodeURIComponent(defect.defect_number || defect.title || 'Defect report')}`} className="text-[#B45309] hover:underline" data-testid="defect-detail-reporter-email">{defect.reporter_email}</a>
            )}
          </p>
        )}
      </div>

      {/* Description */}
      <div className="id-card p-5" data-testid="defect-description-card">
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A] mb-2.5">Description</div>
        <p className="text-[15px] text-[#1A1A1A] leading-relaxed">{defect.description}</p>
      </div>

      {/* Asset */}
      {asset && (
        <div className="id-card p-4 flex items-center gap-3" data-testid="defect-asset-card">
          <div className="w-11 h-11 rounded-[10px] bg-[#F4F2ED] border border-[#E6E2D9] flex items-center justify-center shrink-0">
            <Package className="w-5 h-5 text-[#59554D]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A]">Asset</div>
            <p className="font-bold text-[#1A1A1A] leading-tight">{asset.unit_number || asset.name}</p>
            <p className="text-xs font-mono text-[#8A857A]">{asset.serial_number}</p>
          </div>
          <button
            type="button"
            onClick={() => { onClose?.(); navigate(`/assets?view=${asset.id}`); }}
            className="ml-auto shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-[#59554D] hover:text-[#1A1A1A] transition-colors"
            data-testid="defect-open-asset"
          >
            Open asset <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Photos */}
      {defect.photos?.length > 0 && (
        <div className="id-card p-5" data-testid="defect-photos-card">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A] mb-3">Photos</div>
          <div className="grid grid-cols-2 gap-3">
            {defect.photos.map((photo, index) => (
              <div key={index} className="aspect-square rounded-lg overflow-hidden border border-[#E6E2D9] bg-white">
                <img src={getPhotoSrc(photo, token)} alt={`Defect photo ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Classification */}
      {(isAdmin() || isMechanic()) && (
        <div className="id-card p-5" data-testid="defect-classification-card">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A] mb-3">Classification</div>
          <div className="flex flex-col gap-2">
            <Label>Severity</Label>
            <Select value={defect.severity} onValueChange={(v) => handleUpdate({ severity: v })} disabled={updating || !!defect.work_order_id}>
              <SelectTrigger data-testid="severity-select-edit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {severities.map(s => (<SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#8A857A]">Severity is locked once the defect is on a work order.</p>
          </div>
        </div>
      )}

      {/* Resolve */}
      {defect.work_order_id ? (
        <div className="id-card p-5" data-testid="defect-linked-wo-card">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A] mb-2.5">Resolve</div>
          <p className="text-sm text-[#59554D] mb-3 flex items-center gap-1.5">
            <LinkIcon className="w-4 h-4 text-[#C9A400] shrink-0" />
            Linked to a work order. Status and resolution are managed there.
          </p>
          <Button variant="default" className="w-full" onClick={() => navigate(`/work-orders?view=${defect.work_order_id}`)} data-testid="defect-view-wo-btn">
            View Work Order →
          </Button>
        </div>
      ) : (isAdmin() || isMechanic()) && defect.status !== 'resolved' ? (
        <div className="id-card p-5" data-testid="defect-resolve-card">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A] mb-2.5">Resolve</div>
          <p className="text-sm text-[#59554D] mb-3">This defect is not on a work order yet.</p>
          <Button className="w-full h-12 text-[13px]" variant="default" onClick={() => navigate(`/work-orders?new=true&defect=${defect.id}&asset=${defect.asset_id}`)} data-testid="create-workorder-btn">
            <Wrench className="w-4 h-4 mr-2" />
            Create Work Order
          </Button>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-[#E6E2D9]" />
            <span className="text-xs text-[#8A857A] whitespace-nowrap">or add to an open one</span>
            <div className="flex-1 h-px bg-[#E6E2D9]" />
          </div>
          <Select onValueChange={handleLinkToWorkOrder} disabled={linkingToWO || openWorkOrders.length === 0}>
            <SelectTrigger data-testid="link-to-workorder-select">
              <SelectValue placeholder={linkingToWO ? 'Linking…' : openWorkOrders.length === 0 ? 'No open work orders for this asset' : 'Select open work order…'} />
            </SelectTrigger>
            <SelectContent>
              {openWorkOrders.map(wo => (<SelectItem key={wo.id} value={wo.id}>{wo.title}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {/* Delete */}
      {isAdmin() && onDelete && (
        <div className="id-card p-4" data-testid="defect-delete-card">
          <button
            type="button"
            onClick={onDelete}
            className="w-full h-11 rounded-[10px] border border-[#F1C6C0] bg-white hover:bg-[#FEF2F0] text-[#B91C1C] font-semibold inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="defect-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
            Delete defect
          </button>
          <p className="text-xs text-[#8A857A] text-center mt-2.5">If this defect is linked to a work order, the work order remains. This cannot be undone.</p>
        </div>
      )}
    </div>
  );
};

export const DefectsListPage = () => {
  const [searchParams] = useSearchParams();
  const preSelectedAsset = searchParams.get('asset');
  const [defects, setDefects] = useState([]);
  const PAGE_SIZE = 25;
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [registerTotal, setRegisterTotal] = useState(0);
  const [criticalOpen, setCriticalOpen] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assets, setAssets] = useState([]);
  const [groupBy, setGroupBy] = useState('due_next');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Default to hide resolved
  const [severityFilter, setSeverityFilter] = useState('all');
  const [linkedFilter, setLinkedFilter] = useState('unlinked'); // Default: only show defects without a WO yet
  const [dateFilter, setDateFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [density, setDensity] = useDensity('defects_density');
  const { sortKey, sortDir, toggleSort } = useSortState();
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loadingDefect, setLoadingDefect] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Dynamic lookups
  const { data: severities } = useLookup('defect_severity');
  const { data: statuses } = useLookup('defect_status');
  
  const navigate = useNavigate();
  const { formatDate, formatDateTime } = useSettings();
  const { token } = useAuth();

  // Lookup helper — defect rows render an asset pill under the title so
  // users can see at a glance which machine the defect is against.
  const getAssetDisplay = (assetId) => {
    if (!assetId) return null;
    const a = assets.find((x) => x.id === assetId);
    if (!a) return null;
    return a.unit_number || a.name || a.serial_number || null;
  };

  useEffect(() => {
    (async () => {
      try {
        const [assetsRes, usersRes] = await Promise.all([
          assetsApi.getAll(),
          usersApi.getAll().catch(() => ({ data: [] })),
        ]);
        setAssets(assetsRes.data);
        setUsers(usersRes.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const dateRange = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'today': return { from: startOfDay(now), to: endOfDay(now) };
      case 'this_week': return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'last_week': return { from: startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }) };
      case 'this_month': return { from: startOfMonth(now), to: endOfMonth(now) };
      case 'last_month': return { from: startOfMonth(subMonths(now, 1)), to: endOfMonth(subMonths(now, 1)) };
      default: return null;
    }
  }, [dateFilter]);

  const buildParams = (nextOffset) => {
    const params = { limit: PAGE_SIZE, offset: nextOffset, status: statusFilter };
    if (search.trim()) params.search = search.trim();
    if (severityFilter !== 'all') params.severity = severityFilter;
    if (linkedFilter !== 'all') params.linked = linkedFilter;
    if (dateRange) { params.date_from = dateRange.from.toISOString(); params.date_to = dateRange.to.toISOString(); }
    if (sortKey) { params.sort = sortKey; params.sort_dir = sortDir; }
    return params;
  };

  const loadData = async (reset = true) => {
    const nextOffset = reset ? 0 : offset + PAGE_SIZE;
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const res = await defectsApi.getAll(buildParams(nextOffset));
      const data = res.data;
      setTotal(data.total);
      setRegisterTotal(data.register_total);
      setCriticalOpen(data.critical_open);
      setOffset(nextOffset);
      setDefects((prev) => (reset ? data.items : [...prev, ...data.items]));
    } catch (error) {
      toast.error('Failed to load defects');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => loadData(true), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, severityFilter, linkedFilter, dateFilter, sortKey, sortDir]);

  const loadDefectDetails = async (id) => {
    setLoadingDefect(true);
    try {
      const defectRes = await defectsApi.getById(id);
      setSelectedDefect(defectRes.data);
      
      const asset = assets.find(a => a.id === defectRes.data.asset_id);
      setSelectedAsset(asset || null);
    } catch (error) {
      toast.error('Failed to load defect details');
      closeDrawer();
    } finally {
      setLoadingDefect(false);
    }
  };

  const filteredDefects = defects;
  const assetLabel = (d) => {
    const a = assets.find(x => x.id === d.asset_id);
    return a ? (a.name || a.unit_number || 'Unknown asset') : 'Unknown asset';
  };
  const orderedDefects = groupBy === 'by_asset'
    ? [...filteredDefects].sort((a, b) => assetLabel(a).localeCompare(assetLabel(b)))
    : filteredDefects;
  const hasMore = defects.length < total;

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(prev => prev.length === filteredDefects.length ? [] : filteredDefects.map(d => d.id));
  const createWOFromSelected = () => {
    const objs = defects.filter(d => selectedIds.includes(d.id));
    if (objs.length === 0) return;
    const assetIds = [...new Set(objs.map(d => d.asset_id))];
    if (assetIds.length > 1) {
      toast.error('Those defects are on different assets. A work order covers one asset — select defects from a single asset.');
      return;
    }
    navigate(`/work-orders?new=true&asset=${assetIds[0]}&defects=${selectedIds.join(',')}`);
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('active'); // Reset to active (hide resolved)
    setSeverityFilter('all');
    setLinkedFilter('unlinked'); // Reset to default
    setDateFilter('all');
  };

  const hasActiveFilters = search !== '' || statusFilter !== 'active' || severityFilter !== 'all' || linkedFilter !== 'unlinked' || dateFilter !== 'all';

  const openAddDrawer = () => {
    setSelectedDefect(null);
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const openViewDrawer = async (defectId) => {
    setDrawerMode('view');
    setDrawerOpen(true);
    await loadDefectDetails(defectId);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode(null);
    setSelectedDefect(null);
    setSelectedAsset(null);
  };

  const handleFormSuccess = () => {
    closeDrawer();
    loadData();
  };

  const handleUpdate = async (data) => {
    try {
      const response = await defectsApi.update(selectedDefect.id, data);
      setSelectedDefect(response.data);
      loadData();
      toast.success('Defect updated');
    } catch (error) {
      toast.error('Failed to update defect');
    }
  };

  const handleDelete = async () => {
    if (!selectedDefect) return;
    setDeleting(true);
    try {
      await defectsApi.delete(selectedDefect.id);
      toast.success('Defect deleted');
      setConfirmDeleteOpen(false);
      closeDrawer();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete defect');
    } finally {
      setDeleting(false);
    }
  };

  const getDrawerTitle = () => {
    switch (drawerMode) {
      case 'add': return 'Report Defect';
      case 'view': return 'Defect Details';
      default: return '';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="defects-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Defect Management
          </h1>
          <p className="text-muted-foreground">
            Track and resolve equipment defects
            {criticalOpen > 0 && (
              <span style={{ color: '#B4380F' }} className="font-medium"> · {criticalOpen} critical open</span>
            )}
          </p>
        </div>
        <Button onClick={openAddDrawer} className="btn-industrial" data-testid="create-defect-btn">
          <Plus className="w-5 h-5 mr-2" />
          Report Defect
        </Button>
      </div>

      {/* Filters + list — single card (gold standard) */}
      <Card className="overflow-hidden">
        <div className="p-4 space-y-4 border-b border-border">
          {/* Row 1 — search (with count pill), severity + status dropdowns, view toggle */}
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Title, description, asset or reporter"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-20"
                data-testid="defect-search-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-[#8A857A] pointer-events-none">
                {defects.length} shown
              </span>
            </div>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="severity-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severity</SelectItem>
                {severities.map(s => (<SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="all">All status</SelectItem>
                {statuses.map(s => (<SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-0.5 rounded-md bg-secondary p-0.5 shrink-0" style={{ display: 'none' }}>
              {[{ m: 'list', I: List }].map(({ m, I }) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setViewMode(m)}
                  className={`w-8 h-8 rounded-[6px] flex items-center justify-center transition-colors ${viewMode === m ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`${m}-view-btn`}
                >
                  <I className="w-4 h-4" />
                </button>
              ))}
            </div>
            <DensityToggle density={density} onChange={setDensity} />
            <div className="flex items-center gap-0.5 rounded-md bg-secondary p-0.5 shrink-0" data-testid="defect-groupby-toggle">
              {[{ id: 'due_next', label: 'Recent' }, { id: 'by_asset', label: 'By asset' }].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setGroupBy(opt.id)}
                  className={`px-3 h-8 rounded-[6px] text-sm font-medium transition-colors ${groupBy === opt.id ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`defect-groupby-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2 — Work order + Reported pills, side by side */}
          <div className="flex items-center gap-x-6 gap-y-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A857A]">Work order</span>
              <div className="flex items-center gap-1 bg-secondary rounded-lg p-1 flex-wrap" data-testid="defect-linkage-filters">
                {[
                  { id: 'unlinked', label: 'No work order yet' },
                  { id: 'linked', label: 'On a work order' },
                  { id: 'all', label: 'All' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setLinkedFilter(opt.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${linkedFilter === opt.id ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`defect-linked-filter-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A857A]">Reported</span>
              <div className="flex items-center gap-1 bg-secondary rounded-lg p-1 flex-wrap">
                {DATE_FILTERS.map(filter => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setDateFilter(filter.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${dateFilter === filter.id ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`date-filter-${filter.id}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-xs text-[#8A857A] hover:text-[#1A1A1A] transition-colors"
                data-testid="clear-filters-btn"
              >
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
        </div>

        {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredDefects.length === 0 ? (
        <div className="py-16 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Defects Found</h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters ? 'Try adjusting your filters' : 'All equipment is in good condition'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
      ) : viewMode === 'card' ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
          {filteredDefects.map(defect => (
            <Card 
              key={defect.id} 
              className="card-hover cursor-pointer"
              onClick={() => openViewDrawer(defect.id)}
              data-testid={`defect-card-${defect.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold">{defect.title}</h3>
                    </div>
                    {getAssetDisplay(defect.asset_id) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/assets/${defect.asset_id}`);
                        }}
                        className="inline-flex items-center gap-1 mb-2 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border border-border bg-secondary/60 text-foreground hover:bg-secondary hover:border-primary/40 transition-colors"
                        data-testid={`defect-card-asset-pill-${defect.id}`}
                        title="Open asset"
                      >
                        <Package className="w-3 h-3" />
                        {getAssetDisplay(defect.asset_id)}
                      </button>
                    )}
                    <div className="flex gap-2 mb-2 flex-wrap">
                      <SeverityBadge severity={defect.severity} severities={severities} />
                      <StatusBadge status={defect.status} statuses={statuses} />
                      {defect.work_order_id && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/work-orders?view=${defect.work_order_id}`);
                          }}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                          data-testid={`defect-card-wo-pill-${defect.id}`}
                          title="Open linked work order"
                        >
                          <LinkIcon className="w-3 h-3" />
                          On WO
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{defect.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(defect.created_at)}
                      </span>
                      {defect.assigned_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {defect.assigned_name}
                        </span>
                      )}
                    </div>
                  </div>
                  {defect.photos?.length > 0 && (
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                      <img 
                        src={getPhotoSrc(defect.photos[0], token)}
                        alt="Defect"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
          <div className="overflow-x-auto">
            {selectedIds.length > 0 && (
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#FFF9E6] border-b border-[#F5E3B3]" data-testid="defect-bulk-bar">
                <span className="text-cell font-semibold text-[#1A1A1A]">{selectedIds.length} defect{selectedIds.length === 1 ? '' : 's'} selected</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} data-testid="defect-bulk-clear">Clear</Button>
                  <Button size="sm" onClick={createWOFromSelected} data-testid="defect-bulk-create-wo">
                    <Wrench className="w-3.5 h-3.5 mr-1.5" />
                    Create Work Order from selected
                  </Button>
                </div>
              </div>
            )}
            <table className="data-table w-full" data-density={density}>
                <thead>
                  <tr className="border-b border-border">
                    <th className="p-4 w-10">
                      <input type="checkbox" className="accent-[#FFD108] w-4 h-4 align-middle"
                        checked={filteredDefects.length > 0 && selectedIds.length === filteredDefects.length}
                        onChange={toggleSelectAll} data-testid="defect-select-all" />
                    </th>
                    <SortableTh sortKey="title" label="Title" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="severity" label="Severity" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="status" label="Status" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <th className="text-left p-4">Work Order</th>
                    <SortableTh sortKey="reported_by" label="Reported By" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="reported" label="Reported" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orderedDefects.map((defect, idx, arr) => {
                    const showHeader = groupBy === 'by_asset' && (idx === 0 || arr[idx - 1].asset_id !== defect.asset_id);
                    return (
                    <React.Fragment key={defect.id}>
                    {showHeader && (
                      <tr className="bg-[#FBFAF7] border-b border-[#E6E2D9]" data-testid={`defect-group-${defect.asset_id}`}>
                        <td colSpan={8} className="px-4 py-2">
                          <span className="font-semibold text-[#2A2A2A] text-[12px]">{assetLabel(defect)}</span>
                          <span className="text-muted-foreground text-[12px]"> · {arr.filter(x => x.asset_id === defect.asset_id).length} defects</span>
                        </td>
                      </tr>
                    )}
                    <tr 
                      className="border-b border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                      style={defect.severity === 'critical' && defect.status !== 'resolved' ? { boxShadow: 'inset 3px 0 0 #DC2626' } : undefined}
                      onClick={() => openViewDrawer(defect.id)}
                      data-testid={`defect-row-${defect.id}`}
                    >
                      <td className="p-4 w-10" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="accent-[#FFD108] w-4 h-4 align-middle"
                          checked={selectedIds.includes(defect.id)}
                          onChange={() => toggleSelect(defect.id)}
                          data-testid={`defect-select-${defect.id}`} />
                      </td>
                      <td className="p-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[#1A1A1A]">{defect.title}</span>
                            {getAssetDisplay(defect.asset_id) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/assets?view=${defect.asset_id}`);
                                }}
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-md bg-[#F0EDE6] text-[#59554D] hover:bg-[#E6E2D9] transition-colors"
                                data-testid={`defect-row-asset-pill-${defect.id}`}
                                title="Open asset"
                              >
                                <Package className="w-3 h-3" />
                                {getAssetDisplay(defect.asset_id)}
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-1">{defect.description}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <SeverityBadge severity={defect.severity} severities={severities} />
                      </td>
                      <td className="p-4">
                        <StatusBadge status={defect.status} statuses={statuses} />
                      </td>
                      <td className="p-4">
                        {defect.work_order_id ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/work-orders?view=${defect.work_order_id}`);
                            }}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                            data-testid={`defect-row-wo-pill-${defect.id}`}
                            title="Open linked work order"
                          >
                            <LinkIcon className="w-3 h-3" />
                            On WO
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground" data-testid={`defect-row-no-wo-${defect.id}`}>—</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span data-testid={`defect-row-reporter-${defect.id}`}>
                            {defect.reported_by_name || defect.created_by_name || (
                              <span className="text-muted-foreground">Unknown</span>
                            )}
                          </span>
                          {defect.source === 'public_qr' && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border border-amber-400/40 bg-amber-400/10 text-amber-500"
                              title="Reported via the public QR-code page"
                              data-testid={`defect-row-qr-pill-${defect.id}`}
                            >
                              <QrCode className="w-3 h-3" />
                              QR
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {formatDate(defect.created_at)}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!defect.work_order_id && defect.status !== 'resolved' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/work-orders?new=true&defect=${defect.id}&asset=${defect.asset_id}`); }}
                              data-testid={`defect-raise-wo-${defect.id}`}
                            >
                              <Wrench className="w-3.5 h-3.5 mr-1.5" />
                              Raise WO
                            </Button>
                          )}
                          <ActionMenu 
                            actions={[
                              { label: 'View Details', icon: Eye, onClick: () => openViewDrawer(defect.id) },
                            ]}
                            testId={`defect-${defect.id}`}
                          />
                        </div>
                      </td>
                    </tr>
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
          </div>
        )}

        {!loading && filteredDefects.length > 0 && (
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-border flex-wrap">
            <span className="text-xs text-muted-foreground" data-testid="defect-count-footer">
              Showing {filteredDefects.length} of {total} defects
              {linkedFilter === 'unlinked' ? ' with no work order yet' : linkedFilter === 'linked' ? ' on a work order' : ''}
            </span>
            <div className="flex items-center gap-4">
              {hasMore && (
                <Button variant="outline" size="sm" onClick={() => loadData(false)} disabled={loadingMore} data-testid="defects-load-more">
                  {loadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Load More ({total - filteredDefects.length} more)
                </Button>
              )}
              <span className="text-xs text-muted-foreground">
                Register total: {registerTotal} defect{registerTotal === 1 ? '' : 's'} across all work-order states
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* Slide Drawer */}
      <SlideDrawer
        open={drawerOpen}
        onOpenChange={closeDrawer}
        title={getDrawerTitle()}
        size="lg"
        className="sheet-ground"
      >
        {loadingDefect ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : drawerMode === 'add' ? (
          <DefectForm 
            assets={assets}
            severities={severities}
            preSelectedAsset={preSelectedAsset}
            onSuccess={handleFormSuccess}
            onCancel={closeDrawer}
          />
        ) : drawerMode === 'view' && selectedDefect ? (
          <DefectDetailView 
            defect={selectedDefect}
            asset={selectedAsset}
            users={users}
            severities={severities}
            statuses={statuses}
            onUpdate={handleUpdate}
            onClose={closeDrawer}
            onDelete={() => setConfirmDeleteOpen(true)}
            onRefresh={() => {
              loadDefectDetails(selectedDefect.id);
              loadData();
            }}
          />
        ) : null}
      </SlideDrawer>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={(o) => { if (!o) setConfirmDeleteOpen(false); }}
        title="Delete defect?"
        description={selectedDefect ? `"${selectedDefect.title}" will be permanently removed from your defect register.` : ''}
        footnote="If this defect is linked to a work order, the work order remains. This action cannot be undone."
        loading={deleting}
        onConfirm={handleDelete}
        testId="confirm-delete-defect"
      />
    </div>
  );
};

// Keep these for backward compatibility - they redirect to list page
export const DefectDetailPage = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/defects', { replace: true });
  }, [navigate]);
  return null;
};

export const DefectFormPage = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/defects', { replace: true });
  }, [navigate]);
  return null;
};
